import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import { FormEvent, useState } from 'react'

import { Card, CardContent, CardHeader, CardTitle, Input, PosButton } from '@/components/ui'
import { useSession } from '@/contexts/SessionContext'
import { rows } from '@/db/database'
import { useBarcodeScanner } from '@/hooks/ui/useBarcodeScanner'
import { CameraScanButton } from '@/components/pos/scan/CameraScanButton'
import { LoginPage } from '@/pages/LoginPage'
import { cn } from '@/lib/utils'
import { initials } from '@/utils'

/**
 * Who is at the till: pick your name and enter your PIN, checked on the device with no network call (§15.10 step 3).
 * Staff without a PIN cannot appear here; a manager resets it in the backoffice.
 */
export const LockScreen = () => {
  const { unlock, device } = useSession()
  const [staffId, setStaffId] = useState<string | null>(null)
  const [withPassword, setWithPassword] = useState(false)
  const { data: staff = [] } = useQuery({
    queryKey: ['pin-staff', device?.branch_id],
    queryFn: async () => {
      // This branch's staff (`Branch.staff_ids`) and admins, who work in every branch.
      const [users, branch, roles] = await Promise.all([
        rows('users').toArray(),
        rows('branches').where('_id').equals(String(device?.branch_id)).first(),
        rows('roles').toArray(),
      ])
      const inBranch = new Set(((branch?.staff_ids as string[] | undefined) ?? []).map(String))
      const adminRoles = new Set(roles.filter((r) => r.key === 'admin').map((r) => String(r._id)))
      return users
        .filter((u) => u.offline_pin && !u.deleted_at && (u.status ?? 'active') === 'active')
        .filter(
          (u) =>
            inBranch.has(String(u._id)) || ((u.role_ids as string[] | undefined) ?? []).some((id) => adminRoles.has(String(id)))
        )
        .sort((a, b) => String(a.name).localeCompare(String(b.name)))
    },
  })

  // Staff card (`Staff.barcode`, POS.md §18.8): picks the staff member; the PIN is still asked.
  const onStaffCard = (code: string) => {
    const member = staff.find((s) => s.barcode && String(s.barcode) === code)
    if (member) setStaffId(String(member._id))
    else toast.error('No staff member on this device has that card')
  }
  useBarcodeScanner(onStaffCard, !withPassword)

  if (withPassword) return <LoginPage onBack={() => setWithPassword(false)} />

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!staffId) return
    const form = e.currentTarget
    try {
      await unlock(staffId, String(new FormData(form).get('pin') ?? ''))
    } catch (err) {
      toast.error((err as Error).message)
      form.reset()
    }
  }

  return (
    <div className="flex h-dvh items-center justify-center bg-frame p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle>Who is using this device?</CardTitle>
              <p className="text-sm text-muted-foreground">Tap your name or scan your staff card.</p>
            </div>
            <CameraScanButton onScan={onStaffCard} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid max-h-72 grid-cols-2 gap-2 overflow-y-auto">
            {staff.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setStaffId(String(s._id))}
                className={cn(
                  'flex items-center gap-2 rounded-md border p-2 text-left text-sm transition-colors',
                  staffId === s._id ? 'border-primary bg-primary text-white' : 'border-input bg-card text-ink hover:bg-subdued'
                )}
              >
                <span className="grid size-8 shrink-0 place-items-center rounded-full bg-violet text-xs font-semibold text-white">
                  {initials(String(s.name))}
                </span>
                <span className="truncate">{String(s.name)}</span>
              </button>
            ))}
            {staff.length === 0 && (
              <p className="col-span-2 text-sm text-muted-foreground">
                No staff with a PIN on this device yet. Sign in with a password, or wait for the next sync.
              </p>
            )}
          </div>
          {staffId && (
            <form onSubmit={onSubmit} className="flex gap-2">
              <Input
                name="pin"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                autoFocus
                placeholder="PIN"
                className="h-11 text-center text-lg tracking-[0.5em]"
              />
              <PosButton type="submit" size="touch">
                Unlock
              </PosButton>
            </form>
          )}
          <PosButton variant="ghost" size="default" className="w-full" onClick={() => setWithPassword(true)}>
            Sign in with a password instead
          </PosButton>
        </CardContent>
      </Card>
    </div>
  )
}
